import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useSnackbar } from '@/hooks/global-tools';
import {
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuWrap,
} from '@/components/Approval/@components/ActionMenuSurface/styles';
import { bodyAttrs, collectDocumentCss } from '@/utils/documentCss';
import { PRINT_FIT_ATTR } from '../styles';
import { ActionMenuRoot } from './styles';

/** 더보기 아이콘 액션 메뉴 — 링크 복사 / 인쇄(데스크탑 전용) / 참조인 추가 / 카드사 메일 발송(법인카드) / 회수 / 반려요청 / 편집 / 삭제 / 품의 취소(근태) */

/** 인쇄 미지원 폭. 기준은 형제 메뉴(TranslationMenu)와 동일 유지 */
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

type ActionMenuProps = {
  open: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>; // 더보기 아이콘 버튼 ref — 외부 클릭 판정 시 anchor 자체는 제외 (토글 onClick 처리)
  documentId: string; // 링크 복사가 조립할 doc URL 의 식별자
  printableRef: React.RefObject<HTMLElement | null>; // 인쇄 시 새 창에 복제할 결재 컨테이너 ref
  canRecall?: boolean; // [회수] 항목 노출 여부. 작성자 + status P/H + 누구도 결재 안 함 + apvType ≠ 209
  onRecall?: () => void; // 회수 클릭 시 회수 사유 입력 모달을 여는 핸들러
  canRequestReturn?: boolean; // [반려요청] 항목 노출 여부. 작성자 + status P/H + 결재한 결재자 1명 이상 — 회수와 배타적.
  onRequestReturn?: () => void; // 반려요청 클릭 시 사유 입력 모달을 여는 핸들러
  canAddReferrer?: boolean; // [참조인 추가] 항목 노출 여부. 부모(ApprovalView)가 레거시 조건(security<9 + status∈{P,H,C,R})을 판정해 전달.
  onAddReferrer?: () => void; // [참조인 추가] 클릭 시 모달을 여는 핸들러. 미제공 시 no-op.
  canEdit?: boolean; // [편집] 항목 노출 여부. status S/R + 작성자 본인 — 액션 버튼 row 와 같은 판정을 부모가 전달.
  onEdit?: () => void; // [편집] 클릭 시 편집 화면 전환 핸들러
  canDelete?: boolean; // [삭제] 항목 노출 여부. 편집과 동일 조건
  onDelete?: () => void; // [삭제] 클릭 시 삭제 확인 다이얼로그를 여는 핸들러
  canCancelAttendance?: boolean; // [품의 취소] 항목 노출 여부. 근태 자가결재 + 작성자 본인 + C — 부모가 v1 상세로 판정
  cancelAttendanceDisabled?: boolean; // 취소 불가(cancelType none) — 항목은 두고 비활성 + 안내
  onCancelAttendance?: () => void;
  canSendCardCompanyMail?: boolean; // [카드사 메일 발송] 항목 노출 여부. 법인카드 품의 P·C + 발송 권한 — 부모가 발송 이력 조회로 판정
  sendCardCompanyMailDisabled?: boolean; // 발송 중
  onSendCardCompanyMail?: () => void;
};

/** 스타일시트/이미지 로드 대기 상한 — 초과 시 인쇄를 중단하고 사용자에게 알림 */
const PRINT_ASSET_TIMEOUT_MS = 3000;

/**
 * 인쇄 창의 스타일시트/이미지 로드 대기. 상한 초과 여부를 반환.
 *
 * 적용 전에 print 하면 무스타일·무이미지 상태로 페이지가 매겨짐 — 이미지가 빈 자리로
 * 남는 정도가 아니라 높이 0 으로 잡혀 페이지 구성 자체가 어긋남.
 * 인쇄 창의 <link> 는 인라인 못 한 폴백 시트만이라 링크 대기도 그것에 한정됨.
 */
const waitForPrintAssets = (win: Window): Promise<boolean> => {
  const pending: Promise<void>[] = [];
  const settle = (el: Element) =>
    new Promise<void>((resolve) => {
      el.addEventListener('load', () => resolve(), { once: true });
      el.addEventListener('error', () => resolve(), { once: true });
    });

  win.document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    .forEach((link) => {
      if (!link.sheet) pending.push(settle(link));
    });
  win.document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
    // 인쇄물은 화면 밖 이미지까지 전부 필요 — lazy 로 남겨두면 로드되지 않아
    // 높이 0 으로 페이지가 매겨지고, load/error 도 안 와 대기 상한까지 지연됨
    img.loading = 'eager';
    if (!img.complete) pending.push(settle(img));
  });

  if (!pending.length) return Promise.resolve(false);
  return Promise.race([
    Promise.all(pending).then(() => false),
    new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), PRINT_ASSET_TIMEOUT_MS),
    ),
  ]);
};

/** 인쇄 창 본문 폭 = A4 폭. 화면에서도 종이 폭으로 그려야 넘치는 표를 미리 잴 수 있다 */
const PRINT_PAGE_WIDTH = 794;

/** 종이 폭을 넘는 가로 스크롤 영역에 표시 — 그 안의 표만 인쇄 때 줄인다 */
const markPrintFitAreas = (win: Window) => {
  win.document.body.querySelectorAll<HTMLElement>('*').forEach((el) => {
    if (el.scrollWidth <= el.clientWidth + 1) return;
    const { overflowX } = win.getComputedStyle(el);
    if (overflowX === 'auto' || overflowX === 'scroll') {
      el.setAttribute(PRINT_FIT_ATTR, '');
    }
  });
};

/**
 * 인쇄 헬퍼 — 새 창에 결재 컨테이너 + 현재 페이지 CSS·body 폰트 변수 복제 후 print.
 * [data-print-exclude] 노드는 제외. 팝업 차단 시 현재 창 전체 인쇄 fallback.
 */
const openPrintWindow = (target: HTMLElement, onAssetTimeout: () => void) => {
  const printWindow = window.open('', '_blank', 'width=900,height=1000');
  if (!printWindow) {
    window.print();
    return;
  }
  const { css, links } = collectDocumentCss();
  const targetHtml = target.outerHTML;
  const title = document.title || '';

  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    ${links.join('\n')}
    <style>${css}</style>
    <style>
      @page { size: A4; margin: 0; }
      body { margin: 0; padding: 16px; max-width: ${PRINT_PAGE_WIDTH}px; box-sizing: border-box; background: var(--dk-bg, #fff); }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    </style>
  </head>
  <body${bodyAttrs()}>${targetHtml}</body>
</html>`);
  printWindow.document.close();

  // 인쇄물에 열린 메뉴 레이어가 따라가지 않도록 제거.
  printWindow.document
    .querySelectorAll('[data-print-exclude]')
    .forEach((el) => el.remove());

  const triggerPrint = () => {
    try {
      printWindow.focus();
      printWindow.print();
    } finally {
      printWindow.close();
    }
  };

  void waitForPrintAssets(printWindow).then((timedOut) => {
    if (timedOut) {
      // 미완성 상태로 인쇄하면 레이아웃이 어긋난 인쇄물이 나감 — 중단하고 재시도 안내
      printWindow.close();
      onAssetTimeout();
      return;
    }
    // 로드 완료 직후 레이아웃이 반영되기까지 한 프레임 여유
    setTimeout(() => {
      markPrintFitAreas(printWindow);
      triggerPrint();
    }, 100);
  });
};

const ActionMenu = ({
  open,
  onClose,
  anchorRef,
  documentId,
  printableRef,
  canRecall = false,
  onRecall,
  canRequestReturn = false,
  onRequestReturn,
  canAddReferrer = false,
  onAddReferrer,
  canEdit = false,
  onEdit,
  canDelete = false,
  onDelete,
  canCancelAttendance = false,
  cancelAttendanceDisabled = false,
  onCancelAttendance,
  canSendCardCompanyMail = false,
  sendCardCompanyMailDisabled = false,
  onSendCardCompanyMail,
}: ActionMenuProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  // 메뉴는 open 후에만 렌더돼 SSR 첫 렌더값(false)에 걸리지 않음.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  /** 링크 복사 — 레거시 `data-clipboard-text` URL 패턴 (`/approval/viewer?doc=...`) 그대로 */
  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/approval/viewer?doc=${documentId}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccess({ message: t('링크가 복사되었습니다.') });
    } catch {
      showError({ message: t('링크 복사에 실패했습니다.') });
    }
    onClose();
  }, [documentId, onClose, showError, showSuccess, t]);

  /** 인쇄 — 결재 컨테이너만 새 창에 복제 후 print */
  const handlePrint = useCallback(() => {
    const target = printableRef.current;
    if (target) {
      openPrintWindow(target, () =>
        showError({
          message: t(
            '문서를 불러오지 못해 인쇄를 중단했습니다. 다시 시도해 주세요.',
          ),
        }),
      );
    } else {
      window.print();
    }
    onClose();
  }, [printableRef, onClose, showError, t]);

  if (!open) return null;

  return (
    <ActionMenuRoot ref={rootRef} data-print-exclude="">
      <ActionMenuWrap>
        <ActionMenuContent>
          <ActionMenuItem type="button" onClick={handleCopyLink}>
            {t('링크 복사')}
          </ActionMenuItem>
          {!isMobile && (
            <ActionMenuItem type="button" onClick={handlePrint}>
              {t('인쇄')}
            </ActionMenuItem>
          )}
          {canAddReferrer && (
            <ActionMenuItem
              type="button"
              onClick={() => {
                onAddReferrer?.();
                onClose();
              }}
            >
              {t('참조인 추가')}
            </ActionMenuItem>
          )}
          {canSendCardCompanyMail && (
            <ActionMenuItem
              type="button"
              disabled={sendCardCompanyMailDisabled}
              onClick={() => {
                onSendCardCompanyMail?.();
                onClose();
              }}
            >
              {t('카드사 메일 발송')}
            </ActionMenuItem>
          )}
          {canRecall && (
            <ActionMenuItem
              type="button"
              onClick={() => {
                onRecall?.();
                onClose();
              }}
            >
              {t('회수')}
            </ActionMenuItem>
          )}
          {canRequestReturn && !canRecall && (
            <ActionMenuItem
              type="button"
              onClick={() => {
                onRequestReturn?.();
                onClose();
              }}
            >
              {t('반려요청')}
            </ActionMenuItem>
          )}
          {canEdit && (
            <ActionMenuItem
              type="button"
              onClick={() => {
                onEdit?.();
                onClose();
              }}
            >
              {t('편집')}
            </ActionMenuItem>
          )}
          {canDelete && (
            <ActionMenuItem
              type="button"
              tone="caution"
              onClick={() => {
                onDelete?.();
                onClose();
              }}
            >
              {t('삭제')}
            </ActionMenuItem>
          )}
          {canCancelAttendance && (
            <ActionMenuItem
              type="button"
              tone="caution"
              disabled={cancelAttendanceDisabled}
              title={
                cancelAttendanceDisabled
                  ? t('취소할 수 없는 품의입니다.')
                  : undefined
              }
              onClick={() => {
                onCancelAttendance?.();
                onClose();
              }}
            >
              {t('품의 취소')}
            </ActionMenuItem>
          )}
        </ActionMenuContent>
      </ActionMenuWrap>
    </ActionMenuRoot>
  );
};

export default ActionMenu;
